import type { UI } from '@/v2/types';
import Decimal from 'decimal.js';

import { convertCashToPoints } from '@/lib/pointsPay';
import { Charges } from '@/v2/services/availability/types/api/parsedResponse';

interface PointsCharge {
  total: Charges['total'];
}
interface PointsCharges {
  selectedOption: PointsCharge;
  option: PointsCharge;
  offer?: PointsCharge;
  current: {
    amount: string | number;
    fraction: number;
  };
}

export const getPointsDifference = ({
  charges,
  tiers,
}: {
  charges: Omit<PointsCharges, 'current'>;
  tiers?: UI.QFFPointTiersInstance['tiers'];
}) => {
  const { selectedOption, option, offer } = charges;
  const baseAmount = offer ? offer.total.amount : 0;
  const basePoints = convertCashToPoints(baseAmount, tiers).toNumber();

  const optionAmountDifference = Decimal.sub(
    option.total.amount,
    selectedOption.total.amount,
  );

  const targetAmount = Decimal.add(baseAmount, optionAmountDifference);
  const targetPoints = convertCashToPoints(targetAmount, tiers).toNumber();

  const points = Math.abs(targetPoints - basePoints);

  return points;
};

/*
  This function allows us to generate the points differences between flights,
  without changing the cash value.
  Eg. (assume here that $1 is 1PTS)
  Total is $2,000, split into 1,500 PTS and $500 Cash.
  Another flight route is + $200, but we want to keep the cash at $500,
  this util will take those values into account and return 200PTS
*/
export const getPointsDifferenceStaticCash = ({
  charges,
  tiers,
}: {
  charges: PointsCharges;
  tiers?: UI.QFFPointTiersInstance['tiers'];
}) => {
  const { selectedOption, option, offer } = charges;
  const baseAmount = offer ? offer.total.amount : 0;

  // Difference between selected flight and "other (option)" flight, in $Cash. eg. 200.00
  const flightAmountDiff = Decimal.sub(
    option.total.amount,
    selectedOption.total.amount,
  );

  // The current $Cash that's being displayed - this is static and won't change. eg. 1500.00
  const currentStaticCash = Decimal.mul(
    charges.current.amount,
    1 - charges.current.fraction,
  );

  // Current $Total - static cash above. eg. 500.00
  const totalMinusStaticCash = Decimal.sub(baseAmount, currentStaticCash);

  const currentPoints = convertCashToPoints(
    totalMinusStaticCash,
    tiers,
  ).toNumber();

  // $Total AFTER $Flight (eg. 2294.1)
  const targetAmount = Decimal.add(baseAmount, flightAmountDiff);

  const totalMinusStatic = Decimal.sub(targetAmount, currentStaticCash);

  const targetPointsTotal = convertCashToPoints(
    totalMinusStatic,
    tiers,
  ).toNumber();

  return Math.abs(targetPointsTotal - currentPoints);
};

export default getPointsDifference;
